import { WhatsappAgentLogModel } from "@jugl-web/rest-api/integrations/models";
import { SidebarDrawer } from "@jugl-web/ui-components";
import React, { useEffect, useMemo, useState } from "react";
import { cx, isoToLocalDate, useTranslations } from "@jugl-web/utils";
import format from "date-fns/format";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { customerPhoneStringToDisplay } from "@jugl-web/domain-resources/customers/utils/customerPhoneStringToDisplay";
import { RICH_TEXT_CONTENT_CLASS } from "@jugl-web/domain-resources/common/components/RichTextarea";
import { useWhatsappTemplateBodyRenderer } from "./hooks/useWhatsappTemplateBodyRenderer";
import { AccountLogStatusPill } from "../AccountLogStatusPill";

export const AccountLogDetailsDialog: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
  accountLog?: WhatsappAgentLogModel;
}> = ({ isOpen, onRequestClose, accountLog }) => {
  const { t } = useTranslations();
  const { tasksApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const [messageHtml, setMessageHtml] = useState("");

  // TODO: Add boardId
  const { renderHtml } = useWhatsappTemplateBodyRenderer();

  useEffect(() => {
    if (accountLog) {
      renderHtml({
        body: accountLog.msg.text,
        variables: accountLog.msg.params.map((item) => item.text),
      }).then(setMessageHtml);
    } else {
      setMessageHtml("");
    }
  }, [accountLog, renderHtml]);

  const { data: task, isSuccess } = tasksApi.useGetTaskQuery(
    accountLog?.type === "task"
      ? {
          entityId,
          taskId: accountLog.obj_id,
        }
      : skipToken
  );

  const AccountLogDetailsFields = useMemo(() => {
    if (!accountLog) {
      return [];
    }

    return [
      {
        label: t({
          id: "client-support-page.action-trigger",
          defaultMessage: "Action Trigger",
        }),
        value: <span className="font-semibold">{accountLog.trigger}</span>,
      },
      {
        label: t({
          id: "client-support-page.account-id",
          defaultMessage: "Account ID",
        }),
        value: accountLog.waba_id,
      },
      {
        label: t({
          id: "client-support-page.date",
          defaultMessage: "Date",
        }),
        value: format(
          isoToLocalDate(accountLog.inserted_at),
          "d MMM yyyy, h:mm a"
        ),
      },
      {
        label: t({
          id: "client-support-page.receiver-name",
          defaultMessage: "Receiver Name",
        }),
        value: (
          <span className="text-primary-800 underline">
            {accountLog.recipient_name}
          </span>
        ),
      },
      {
        label: t({
          id: "client-support-page.receiver-customer",
          defaultMessage: "Receiver Customer",
        }),
        value: (() => customerPhoneStringToDisplay(accountLog.mobile))(),
      },
      ...(task && isSuccess
        ? [
            {
              label: t({
                id: "client-support-page.related-item",
                defaultMessage: "Related Item",
              }),
              value: (
                <span className="text-primary-800 underline">{task.name}</span>
              ),
            },
          ]
        : []),
      {
        label: t({
          id: "client-support-page.status",
          defaultMessage: "Status",
        }),
        value: <AccountLogStatusPill status={accountLog.status} />,
      },
    ];
  }, [accountLog, t, task, isSuccess]);

  return (
    <SidebarDrawer
      isOpen={isOpen}
      onClose={onRequestClose}
      title="Activity Log Details"
    >
      <SidebarDrawer.Content className="flex flex-col px-[50px] py-10">
        {AccountLogDetailsFields.map((el, idx) => (
          <React.Fragment key={el.label}>
            <div className="flex items-center">
              <span className="w-[200px] shrink-0 text-dark-700">
                {el.label}
              </span>
              <div className="overflow-hidden font-secondary wrap-break-word text-dark">
                {el.value}
              </div>
            </div>
            {AccountLogDetailsFields.length - 1 !== idx && (
              <div className="my-4 h-px w-full bg-grey-200" />
            )}
          </React.Fragment>
        ))}
        <div className="mt-12 flex flex-col gap-2">
          <span className="ml-2.5 truncate font-secondary text-[15px] text-dark-500">
            {t({
              id: "client-support-page.sent-message",
              defaultMessage: "Sent Message",
            })}
          </span>
          <div
            className={cx(
              "overflow-hidden rounded-[10px] border border-dashed border-grey-400 bg-[#FAFAFA] px-5 py-4 font-secondary leading-[150%] wrap-break-word text-dark",
              RICH_TEXT_CONTENT_CLASS
            )}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: messageHtml }}
          />
        </div>
      </SidebarDrawer.Content>
    </SidebarDrawer>
  );
};
